import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import {
  AditionInitialization,
  AditionTagPlacement,
  AditionTagWithFallback,
} from '@vgn-medien-holding/vgn-fe-components';
import { getCookie } from 'cookies-next';
import { Link } from '@components/atoms/Link/Link';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { Title } from '@components/atoms/Title';
import { EntryCarousel } from '@components/molecules/EntryCarousel/EntryCarousel';
import { EntityMix } from '@components/organisms/EntityMix/EntityMix';
import { providers as curatedProviders } from '@utils/providers';
import { GetGenresDocument, GetProvidersDocument } from '@lib/graphql/generated';
import { createUrqlClient } from '@lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';
import { IconBall } from '@assets/icon-ball';
import { IconPhone } from '@assets/icon-phone';

// Unterseite Provider
const ad_settings_UnterseiteProvider = {
  billboard: {
    id: 5256689,
    name: 'TVM_Unterseite_Provider_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_top: {
    id: 5256605,
    name: 'TVM_Mobile_Unterseite_Provider_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle1: {
    id: 5256610,
    name: 'TVM_Mobile_Unterseite_Provider_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
  mobile_middle2: {
    id: 5256605,
    name: 'TVM_Mobile_Unterseite_Provider_HPA',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

export function ProviderDetails({ data }) {
  const { genres, providers, oewa } = data;
  const [activeProvider, setActiveProvider] = useState(data?.activeProvider || genres?.data?.[0]);
  const [providerTarget, setProviderTarget] = useState(data?.activeProvider?.id);
  const [sortOrder, setSortOrder] = useState(null);
  const router = useRouter();
  const [filters, setFilters] = useState({
    entityType: null,
    genre: null,
  });

  useEffect(() => {
    if (router.isReady) {
      setFilters({
        entityType: router.query.entityType,
        genre: genres?.data?.find((e) => e.id === router.query.genre)?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  useEffect(() => {
    if (router.isReady && activeProvider?.slug) {
      router.replace(
        {
          pathname: '/provider/[slug]',
          query: {
            slug: activeProvider.slug,
            entityType: filters?.entityType,
            genre: filters?.genre,
          },
        },
        null,
        { shallow: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeProvider, router.isReady]);

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Provider',
    titel: activeProvider?.name,
    assetID: oewa?.id,
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: oewa?.page_key || 'Service',
    oewaSubkontingent: oewa?.category_key || 'Programmeundlokalfuehrer',
    oewaProfilingkontingent: oewa?.profile_key || 'TVProgramme',
    cookieAlert: getCookie('OptanonConsent'),
  });

  return (
    <>
      <Oewa
        pageKey={oewa?.page_key || 'Service'}
        categoryKey={oewa?.category_key || 'Programmeundlokalfuehrer'}
        profileKey={oewa?.profile_key || 'TVProgramme'}
      />
      <Head>{Meta(activeProvider?.metadata, activeProvider?.name)}</Head>
      <AditionInitialization data={{ ad_settings: ad_settings_UnterseiteProvider }} />
      <AditionTagPlacement
        tag={ad_settings_UnterseiteProvider.mobile_top}
        classProps={{ root: 'mt-8 w-full grid place-items-center md:hidden' }}
      />
      <div className="grid">
        <div className="row-start-3 mt-16 grid w-full min-w-0 flex-wrap gap-4 justify-self-end md:row-start-auto md:mt-0 md:flex md:justify-center md:px-3">
          <select
            className="input-tvm-classic md:giant"
            value={filters.genre || ''}
            onChange={(item) => {
              setFilters((prev) => ({ ...prev, genre: item?.target.value }));
            }}
          >
            <option value="">Alle Genres</option>
            {genres?.data?.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.title}
              </option>
            ))}
          </select>
          <select
            className="input-tvm-classic md:giant"
            value={filters?.entityType || ''}
            onChange={(item) => {
              setFilters((prev) => ({ ...prev, entityType: item?.target.value }));
            }}
          >
            <option value="all">Alle Inhalte</option>
            <option value="movies">Filme</option>
            <option value="shows">Serien</option>
          </select>
          <div className="hidden items-center px-2 text-2xl font-semibold text-gray-500 md:grid">auf</div>
          <select
            className="input-tvm-classic md:giant"
            value={activeProvider?.id || ''}
            onChange={(item) => {
              setProviderTarget(item?.target.value);
            }}
          >
            {providers?.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>
        </div>
        <div className="row-start-1 mt-1 md:row-start-auto">
          {providers?.length > 0 && (
            <EntryCarousel
              target={providerTarget}
              type="image"
              primaryProperty="logo"
              entries={providers}
              altProperty="name"
              classProps={{
                entry:
                  'relative flex items-center justify-center content-center w-full h-full max-w-[150px] max-h-20 md:max-w-[250px] md:max-h-28',
                entryContainer:
                  'w-full aspect-video rounded-xl border-gray-650/65 streaming-entry-bg flex items-center justify-center content-center text-3xl',
              }}
              onEntryChange={(provider) => {
                setProviderTarget(null);
                setActiveProvider(provider);
              }}
            />
          )}
        </div>

        <aside>
          <AditionTagWithFallback
            tag={ad_settings_UnterseiteProvider.billboard}
            fallback={ad_settings_UnterseiteProvider.mobile_middle1}
            breakpoint={'lg'}
            classProps={{ root: 'my-5 grid place-items-center' }}
          />
        </aside>
        <div className="row-start-2 mx-auto mt-16 grid max-w-[870px] gap-4 border-y border-gray-650/65 bg-gray-820/70 p-6 md:row-start-auto md:mt-0 md:grid-cols-[1fr,2fr]">
          <div>
            <Title level={1} classProps={{ heading: 'text-2xl lg:text-2xl' }}>
              {activeProvider?.name}
            </Title>
            <div className="mt-5 flex flex-wrap gap-x-7 gap-y-2 text-sm md:grid">
              {activeProvider?.url && (
                <div className="flex items-center text-white">
                  <IconBall classProps={{ root: 'w-4 h-4 mr-2' }} />{' '}
                  <Link href={activeProvider.url}>{activeProvider.url}</Link>
                </div>
              )}
              {activeProvider?.phone && (
                <div className="flex items-center">
                  <IconPhone classProps={{ root: 'w-4 h-4 mr-1' }} /> <span>{activeProvider.phone}</span>
                </div>
              )}
            </div>
          </div>
          <div className="h-px w-full bg-gray-650/25 md:hidden"></div>
          <div>
            <p>Hier kannst Du alles entdecken, was derzeit auf {activeProvider?.name} verfügbar ist.</p>
            <p className="text-gray-400">
              Wähle oben Dein bevorzugtes Genre und entscheide, ob Du dich für Filme oder Serien interessierst.
              Durchstöbere dann einfach die Vorschläge. Falls Du nicht fündig wirst, kannst Du auch einfach jederzeit
              oben den Streaming-Anbieter wechseln.
            </p>
          </div>
        </div>
        <div className="mt-4 grid md:mt-24 md:block">
          <select
            className="input-tvm-classic"
            onChange={(item) => {
              try {
                setSortOrder(JSON.parse(item?.target.value));
              } catch (err) {
                setSortOrder({ orderBy: 'year', orderDirection: 'DESC' });
              }
            }}
          >
            <option value='{"orderBy": "year", "orderDirection": "DESC"}'>Sortieren</option>
            <option value='{"orderBy": "year", "orderDirection": "DESC"}'>Jahr absteigend</option>
            <option value='{"orderBy": "year", "orderDirection": "ASC"}'>Jahr aufsteigend</option>
            <option value='{"orderBy": "title", "orderDirection": DESC"}'>Title absteigend</option>
            <option value='{"orderBy": "title", "orderDirection": "ASC"}'>Title aufsteigend</option>
          </select>
        </div>
      </div>
      <article className="mt-10">
        <EntityMix
          movieVariables={{
            first: 20,
            page: 1,
            provider: activeProvider ? [activeProvider?.id] : [],
            genres: filters.genre ? [filters.genre] : [],
            orderBy: sortOrder?.orderBy,
            orderDirection: sortOrder?.orderDirection,
          }}
          movieSkip={filters?.entityType === 'shows' || !activeProvider}
          tvshowVariables={{
            first: 20,
            page: 1,
            provider: activeProvider ? [activeProvider?.id] : [],
            genres: filters.genre ? [filters.genre] : [],
            orderBy: sortOrder?.orderBy,
            orderDirection: sortOrder?.orderDirection,
          }}
          tvshowSkip={filters?.entityType === 'movies' || !activeProvider}
        >
          <div className="py-8 text-center text-2xl">Leider liegen für diese Auswahl momentan keine Inhalte vor.</div>
        </EntityMix>
      </article>
      <aside>
        <AditionTagPlacement
          tag={ad_settings_UnterseiteProvider.mobile_middle2}
          classProps={{ root: 'my-5 grid place-items-center md:hidden' }}
        />
      </aside>
    </>
  );
}

export const providerDetailsSsrLogic = async ({ slug }) => {
  const { ssrQuery, ssrCache } = createUrqlClient();
  const { data: providersData } = await ssrQuery({
    query: GetProvidersDocument,
    variables: { first: 999, page: 1 },
  });
  const providers = providersData?.providers?.data;

  const { data: genresData } = await ssrQuery({
    query: GetGenresDocument,
    variables: { first: 100, page: 1 },
  });
  const genres = genresData?.genres;

  const localProviders = [];

  curatedProviders.forEach((p) => {
    const el = providers?.find((e) => e.id === p.id);
    if (!el) return;
    const newEl = { ...el, ...p };
    localProviders.push(newEl);
  });

  const activeProvider = localProviders.find((e) => e.slug === slug);

  const data = {
    providers: localProviders,
    activeProvider: activeProvider || null,
    genres,
  };

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: {
      data,
      urqlState: ssrCache.extractData(),
    },
  };
};
